import Axios from '../configs/axios.config';

// Default chunk size (1MB)
export const DEFAULT_CHUNK_SIZE = 1024 * 1024; // 1MB in bytes
export const MAX_RETRY_ATTEMPTS = 3;
export const RETRY_DELAY = 1000; // 1 second

/**
 * Split a file into chunks
 * @param {File} file - The file to be chunked
 * @param {number} chunkSize - Size of each chunk in bytes (default: 1MB)
 * @returns {Object} - Contains chunks array and total count
 */
export const splitFileIntoChunks = (file, chunkSize = DEFAULT_CHUNK_SIZE) => {
  const chunks = [];
  const totalChunks = Math.ceil(file.size / chunkSize);
  
  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);
    
    chunks.push({
      index: i,
      data: chunk,
      size: chunk.size,
      start,
      end
    });
  }
  
  return {
    chunks,
    totalChunks,
    fileSize: file.size
  };
};

/**
 * Create FormData for chunk upload
 * @param {string} fileId - Unique file identifier
 * @param {number} index - Chunk index
 * @param {string} fileName - Original file name
 * @param {number} totalChunks - Total number of chunks
 * @param {Blob} chunkData - Binary chunk data
 * @param {string} folderId - Optional folder ID for organization
 * @returns {FormData} - FormData ready for upload
 */
export const createChunkFormData = (fileId, index, fileName, totalChunks, chunkData, folderId = null) => {
  const formData = new FormData();
  formData.append('file_id', fileId);
  formData.append('index', index.toString());
  formData.append('file_name', fileName);
  formData.append('total_chunks', totalChunks.toString());
  formData.append('chunk', chunkData);
  
  // Add folderId if provided
  if (folderId) {
    formData.append('folder_id', folderId);
  } else {
  }
  
  return formData;
};

/**
 * Upload a single chunk with retry mechanism
 * @param {string} fileId - Unique file identifier
 * @param {number} index - Chunk index
 * @param {string} fileName - Original file name
 * @param {number} totalChunks - Total number of chunks
 * @param {Blob} chunkData - Binary chunk data
 * @param {string} uploadUrl - Upload endpoint URL
 * @param {Function} onProgress - Progress callback function
 * @param {number} retryCount - Current retry attempt
 * @returns {Promise} - Upload promise
 */
export const uploadChunk = async (
  fileId,
  index,
  fileName,
  totalChunks,
  chunkData,
  uploadUrl = '/service/upload-chunk', // Default endpoint
  onProgress = null,
  retryCount = 0,
  folderId = null // Add folderId parameter
) => {
  const formData = createChunkFormData(fileId, index, fileName, totalChunks, chunkData, folderId);
  
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress({
          fileId,
          chunkIndex: index,
          chunkProgress: percentCompleted,
          loaded: progressEvent.loaded,
          total: progressEvent.total
        });
      }
    }
  };

  try {
    const response = await Axios.post(uploadUrl, formData, config);
    return response.data;
  } catch (error) {
    console.error(`Error uploading chunk ${index} for file ${fileId}:`, error);
    
    // Retry logic
    if (retryCount < MAX_RETRY_ATTEMPTS) {
      
      // Wait before retry with exponential backoff
      await new Promise(resolve => 
        setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount))
      );
      
      return uploadChunk(
        fileId,
        index,
        fileName,
        totalChunks,
        chunkData,
        uploadUrl,
        onProgress,
        retryCount + 1,
        folderId // Pass folderId in retry
      );
    }
    
    throw error;
  }
};

/**
 * Upload file in chunks sequentially
 * @param {File} file - File to upload
 * @param {string} fileId - Unique file identifier
 * @param {Object} options - Upload options
 * @returns {Promise} - Upload completion promise
 */
export const uploadFileInChunks = async (file, fileId, options = {}) => {
  const {
    chunkSize = DEFAULT_CHUNK_SIZE,
    uploadUrl = '/service/upload-chunk',
    startChunkIndex = 0, // Index to start uploading from (for resume)
    onProgress = null,
    onChunkComplete = null,
    onError = null,
    signal = null // AbortController signal for cancellation
  } = options;

  try {
    // Split file into chunks
    const { chunks, totalChunks } = splitFileIntoChunks(file, chunkSize);
    
    
    let lastChunkResponse = null; // Store the response from the last chunk
    
    // Upload chunks sequentially (starting from startChunkIndex for resume)
    for (let i = startChunkIndex; i < chunks.length; i++) {
      // Check if upload was cancelled
      if (signal && signal.aborted) {
        throw new Error('Upload cancelled by user');
      }
      
      const chunk = chunks[i];
      
      try {
        // Capture the response from each chunk upload
        const chunkResponse = await uploadChunk(
          fileId,
          chunk.index,
          file.name,
          totalChunks,
          chunk.data,
          uploadUrl,
          onProgress,
          0, // retryCount
          options.folderId // Pass folderId from options
        );
        
        // Store the response from the last chunk (which should contain the final file path)
        if (chunk.index === totalChunks - 1) {
          lastChunkResponse = chunkResponse;
        }
        
        // Notify chunk completion with path if available
        if (onChunkComplete) {
          onChunkComplete({
            fileId,
            chunkIndex: chunk.index,
            totalChunks,
            progress: Math.round(((chunk.index + 1) / totalChunks) * 100),
            path: chunkResponse?.path || chunkResponse?.file_path || null
          });
        }
        
      } catch (chunkError) {
        console.error(`Failed to upload chunk ${chunk.index}:`, chunkError);
        
        if (onError) {
          onError({
            fileId,
            chunkIndex: chunk.index,
            error: chunkError,
            message: `Failed to upload chunk ${chunk.index + 1} of ${totalChunks}`
          });
        }
        
        throw chunkError;
      }
    }
    
    
    // Return the file path and URL from the last chunk response
    const filePath = lastChunkResponse?.path || lastChunkResponse?.file_path || null;
    const fileUrl = lastChunkResponse?.url || null;
    
    return { 
      success: true, 
      fileId, 
      fileName: file.name,
      path: filePath,
      url: fileUrl
    };
    
  } catch (error) {
    console.error(`Upload failed for ${file.name}:`, error);
    
    if (onError) {
      onError({
        fileId,
        error,
        message: `Upload failed: ${error.message}`
      });
    }
    
    throw error;
  }
};

/**
 * Upload multiple files concurrently (with limit)
 * @param {FileList|Array} files - Files to upload
 * @param {Object} options - Upload options
 * @returns {Promise} - Promise that resolves when all uploads complete
 */
export const uploadMultipleFiles = async (files, options = {}) => {
  const {
    concurrency = 2, // Maximum concurrent uploads
    onFileStart = null,
    onFileComplete = null,
    onFileError = null,
    onAllComplete = null,
    ...uploadOptions
  } = options;

  const fileArray = Array.from(files);
  const results = [];
  const errors = [];
  
  // Process files in batches based on concurrency limit
  for (let i = 0; i < fileArray.length; i += concurrency) {
    const batch = fileArray.slice(i, i + concurrency);
    
    const batchPromises = batch.map(async (file) => {
      const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      try {
        if (onFileStart) {
          onFileStart({ fileId, fileName: file.name, fileSize: file.size });
        }
        
        const result = await uploadFileInChunks(file, fileId, {
          ...uploadOptions,
          onError: (error) => {
            if (onFileError) {
              onFileError({ ...error, fileName: file.name });
            }
          }
        });
        
        results.push(result);
        
        if (onFileComplete) {
          onFileComplete(result);
        }
        
        return result;
        
      } catch (error) {
        const errorInfo = {
          fileId,
          fileName: file.name,
          error,
          message: error.message
        };
        
        errors.push(errorInfo);
        
        if (onFileError) {
          onFileError(errorInfo);
        }
        
        throw errorInfo;
      }
    });
    
    // Wait for current batch to complete before processing next batch
    await Promise.allSettled(batchPromises);
  }
  
  const finalResult = {
    successful: results.length,
    failed: errors.length,
    total: fileArray.length,
    results,
    errors
  };
  
  if (onAllComplete) {
    onAllComplete(finalResult);
  }
  
  return finalResult;
};

/**
 * Validate file before upload
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @returns {Object} - Validation result
 */
export const validateFile = (file, options = {}) => {
  const {
    maxSize = 100 * 1024 * 1024, // 100MB default
    allowedTypes = [], // Empty array means all types allowed
    maxNameLength = 255
  } = options;

  const errors = [];

  // Check file size
  if (file.size > maxSize) {
    errors.push(`File size (${formatFileSize(file.size)}) exceeds maximum allowed size (${formatFileSize(maxSize)})`);
  }

  // Check file type
  if (allowedTypes.length > 0) {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const mimeType = file.type.toLowerCase();
    
    const isTypeAllowed = allowedTypes.some(type => {
      if (type.startsWith('.')) {
        return fileExtension === type.substring(1);
      }
      return mimeType.includes(type);
    });
    
    if (!isTypeAllowed) {
      errors.push(`File type not allowed. Allowed types: ${allowedTypes.join(', ')}`);
    }
  }

  // Check file name length
  if (file.name.length > maxNameLength) {
    errors.push(`File name too long (max ${maxNameLength} characters)`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Calculate estimated time remaining
 * @param {number} uploadedBytes - Bytes already uploaded
 * @param {number} totalBytes - Total bytes to upload
 * @param {number} startTime - Upload start timestamp
 * @returns {string} - Formatted time remaining
 */
export const calculateTimeRemaining = (uploadedBytes, totalBytes, startTime) => {
  if (uploadedBytes === 0) return 'Calculating...';
  
  const elapsedTime = Date.now() - startTime;
  const uploadSpeed = uploadedBytes / elapsedTime; // bytes per millisecond
  const remainingBytes = totalBytes - uploadedBytes;
  const remainingTime = remainingBytes / uploadSpeed; // milliseconds
  
  if (remainingTime < 60000) { // Less than 1 minute
    return `${Math.ceil(remainingTime / 1000)}s remaining`;
  } else if (remainingTime < 3600000) { // Less than 1 hour
    return `${Math.ceil(remainingTime / 60000)}m remaining`;
  } else {
    return `${Math.ceil(remainingTime / 3600000)}h remaining`;
  }
};
